import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form } from 'react-bootstrap';
import i18n from 'i18n';
import { Service } from 'types/rest/assetCatalogTypes';
import { fetchServices } from 'store/services/servicesActions';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { handleFieldChange } from 'utils/mockServiceUtils';
import { RouteComponentProps, withRouter } from 'react-router';
import { isPrivateCloud } from 'utils/bzmUserConfigUtil';
import _ from 'lodash';
import axios from 'axios/axios-base';
import { pollTrackingService } from 'axios/axios-polling';
import { setPageFilter } from 'store/ui/pageFilters/pageFilterActions';
import { reloadWorkspaceComponents } from 'store/blazeUserConfig/blazeUserConfigActions';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { formattedTextMultiArgs } from 'utils/commonUtil';
import SimpleLoader from '../../loaders/SimpleLoader';
import Autofocus from '../../forms/Autofocus/Autofocus';
import FindReplaceList, { FindReplaceItem } from 'components/transactionEditor/FindReplaceList';

interface LocalProps {
  service: Service;
  closeModalAction: (keepModalOpen: boolean) => void;
  clonedServiceName: string;
  findAndReplaceToggle?: boolean;
  createMsToggle?: boolean;
  findText?: string;
  replaceText?: string;
}

class CloneServiceModal extends Component<AllProps> {
  private SERVICE_NAME_MAX_LENGTH = 250;

  state = {
    oldServiceName: this.props.clonedServiceName,
    findAndReplaceToggle: false,
    createMsToggle: false,
    findText: '',
    replaceText: '',
    isServiceNameValid: true,
    fullMatchFound: false,
    invalidErrorMessage: '',
    cloningInProgress: false,
    showFindError: false,
    isServiceNameEmpty: false,
    findReplaceList: [],
  };

  /**
   * Search input field value change handler.
   * @param event
   */
  handleNameChange = (event: any) => {
    const keyword: string = event.currentTarget.value as string;
    if (_.isEmpty(keyword)) {
      this.setState({ isServiceNameEmpty: true });
    } else {
      this.setState({ isServiceNameEmpty: false });
    }
    const elements = handleFieldChange(event, this.SERVICE_NAME_MAX_LENGTH, this.props.services);
    this.setState({
      oldServiceName: elements.searchTerm,
      fullMatchFound: elements.fullMatchFound,
      isServiceNameValid: elements.isServiceNameValid,
      invalidErrorMessage: elements.invalidErrorMessage,
    });
  };

  /**
   * Clone Service change handler.
   * Creates new service and clone service using newly created service
   */
  cloneServiceHandler = async () => {
    let newServiceId = -1;
    if (this.state.findAndReplaceToggle && !(this.state.findReplaceList.length > 0)) {
      this.setState({ showFindError: true });
      return;
    } else if (
      !this.state.findAndReplaceToggle ||
      (this.state.findAndReplaceToggle && this.state.findReplaceList.length > 0)
    ) {
      this.setState({ showFindError: false, cloningInProgress: true });

      try {
        if (this.props.service && this.props.service.id) {
          const addFindReplace = [];
          this.state.findReplaceList.forEach(rep => {
            addFindReplace.push({
              find: rep.find,
              replace: {
                replace: rep.replace === undefined || rep.replace === null ? '' : rep.replace,
                type: rep.location,
              },
            });
          });

          let cloneBody: any;
          if (!_.isEmpty(addFindReplace)) {
            cloneBody = {
              serviceId: this.props.service.id,
              newServiceName: this.state.oldServiceName.trim(),
              findAndReplace: addFindReplace,
            };
          } else {
            cloneBody = {
              serviceId: this.props.service.id,
              newServiceName: this.state.oldServiceName.trim(),
            };
          }
          axios
            .post('/workspaces/{workspaceId}/services/clone', cloneBody)
            .then(async result => {
              const cloneSvcResponse = result.data;
              if (!cloneSvcResponse || !cloneSvcResponse.result || !cloneSvcResponse.result.trackingId) {
                throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
              }
              const trackingId = cloneSvcResponse.result.trackingId;

              // Poll for up to ~10 minutes (600 retry attempts with 1 second timeout between each)
              // until the service is cloned
              const trackingResult = await pollTrackingService(trackingId);
              const errorMsg = _.get(trackingResult, 'data.result.errors');
              if (!_.isEmpty(errorMsg)) {
                throw new Error(errorMsg);
              }
              if (
                trackingResult.data &&
                trackingResult.data.result &&
                trackingResult.data.result.data &&
                trackingResult.data.result.data.value &&
                trackingResult.data.result.data.value.newServiceId
              ) {
                newServiceId = trackingResult.data.result.data.value.newServiceId;
                this.props.setPageFilter({
                  searchTerm: '',
                  serviceId: trackingResult.data.result.data.value.newServiceId || -1,
                });
                if (!_.isEmpty(trackingResult) && this.state.createMsToggle) {
                  const status = _.get(trackingResult, 'data.result.status');
                  if (status === 'FINISHED') {
                    this.props.closeModalAction(false);
                    this.props.history.push('/mock-services');
                    this.props.reloadWorkspaceComponents(
                      newServiceId,
                      this.props.service.name,
                      this.state.oldServiceName,
                    );
                    this.setState({ createMsToggle: false });
                  }
                } else {
                  this.props.closeModalAction(false);
                  this.props.reloadWorkspaceComponents(newServiceId);
                  this.props.showToastNotification(
                    ToastType.Success,
                    '',
                    formattedTextMultiArgs(
                      i18n.t('successMsgs.createServiceMsg'),
                      i18n.t('successMsgs.createServiceClonedMsg'),
                      this.props.service.name,
                      this.state.oldServiceName,
                    ),
                  );
                }
              } else {
                throw new Error('Unable to parse tracking object of clone service operation');
              }
            })
            .catch(error => {
              const errorMsg = _.get(error, 'response.data.error', error.toString());
              //If the service name is occupied, do inline validation. Otherwise show error toast
              if (errorMsg.includes('is not unique')) {
                this.setState({ fullMatchFound: true });
              } else {
                this.props.showToastNotification(
                  ToastType.Danger,
                  '',
                  i18n.t('errorMsgs.serviceCreationFailed', {
                    error: errorMsg,
                  }),
                );
              }
              this.setState({ cloningInProgress: false });
              this.props.closeModalAction(true);
            });
          this.props.fetchServices(true);
        }
      } catch (e) {
        this.setState({ cloningInProgress: false });
        this.props.closeModalAction(true);
      } finally {
        this.props.fetchServices(true);
      }
    }
  };

  /**
   * Clone Modal cancel change handler.
   */
  onCancel = () => {
    this.props.closeModalAction(false);
  };

  /**
   * Find and Replace toggle change handler.
   */
  handleToggleChange = () => {
    const list = this.state.findAndReplaceToggle === false ? [{ id: 'find-replace-item-0' } as FindReplaceItem] : [];
    this.setState({
      findAndReplaceToggle: !this.state.findAndReplaceToggle,
      findReplaceList: list,
    });
  };

  /**
   * Create MockService toggle change handler.
   */
  handleMsToggleChange = () => {
    this.setState({
      createMsToggle: !this.state.createMsToggle,
    });
  };

  /**
   * Find input field change handler.
   * @param event
   */
  findChangedHandler = (event: any) => {
    const keyword: string = event.currentTarget.value as string;
    if (!(keyword.trim().length > 0)) {
      this.setState({ showFindError: true });
    } else {
      this.setState({ showFindError: false });
    }
    this.setState({ findText: keyword });
  };

  /**
   * Replace input field change handler.
   * @param event
   */
  replaceChangedHandler = (event: any) => {
    const replaceText: string = event.currentTarget.value as string;
    this.setState({ replaceText: replaceText });
  };

  warningMessages = (isServiceNameValid: boolean, fullMatchFound: boolean, isServiceNameEmpty: boolean) => {
    let errMsg = '';
    if (isServiceNameEmpty) {
      errMsg = i18n.t('servicePicker.serviceNameIsRequired');
    }
    if (!isServiceNameValid) {
      errMsg = i18n.t('servicePicker.serviceNameIsInvalid');
    } else if (fullMatchFound) {
      errMsg = i18n.t('servicePicker.serviceNameIsDuplicate');
    }
    return (
      <div
        className="invalid-feedback"
        style={{
          visibility: this.state.fullMatchFound || !isServiceNameValid || isServiceNameEmpty ? 'visible' : 'hidden',
          height: '12px',
        }}
      >
        {errMsg}
      </div>
    );
  };

  getCloneCss = () => {
    if (this.state.cloningInProgress) {
      return { width: '20%' };
    } else {
      return;
    }
  };

  validFindReplaceList = (findReplaceList: FindReplaceItem[] | null) => {
    let valid = true;
    if (findReplaceList != null && findReplaceList.length > 0) {
      const emptyFields = findReplaceList.filter(fr => {
        if (fr.find === undefined || fr.find.toString().trim() === '') {
          return true;
        }
        if (fr.location !== undefined && fr.location.length > 0) {
          return fr.location.filter(l => l.length === 0).length > 0;
        } else {
          return true;
        }
      });
      valid = emptyFields.length === 0;
    }
    return valid;
  };

  validateToggle = (
    toggle: boolean,
    findReplaceList: FindReplaceItem[] | null,
    fullMatchFound: boolean,
    isServiceNameValid: boolean,
    isServiceNameEmpty: boolean,
  ) => {
    if (isServiceNameValid || isServiceNameEmpty) return true;
    if (fullMatchFound) return true;
    if (toggle) {
      return !this.validFindReplaceList(findReplaceList);
    }
  };

  render() {
    const isValid = this.state.isServiceNameValid ? '' : 'is-invalid';
    const isDuplicate = this.state.fullMatchFound ? 'is-invalid' : '';

    return (
      <Modal show={true} centered={true} backdrop="static" onHide={this.onCancel} size={'lg'}>
        <div className="modal-header">
          <h5 className="modal-title" id="qa-clone-service-modal-title">
            {i18n.t('servicePicker.cloneService')}
          </h5>
          <button
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label={i18n.t('common:actions.close')}
            id={'qa-clone-service-modal-btn-close-icon'}
            title={i18n.t('common:actions.close')}
            onClick={this.onCancel}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <Modal.Body id={'qa-clone-modal-body-text'}>
          <div>
            <Form.Group>
              <p>{i18n.t('servicePicker.cloneServiceSubTitle')}</p>
              <Form.Label htmlFor={'tx-replace-field-label'}>{i18n.t('servicePicker.name')}</Form.Label>
              <Autofocus>
                <Form.Control
                  as="input"
                  id={'qa-clone-service-name-field-id'}
                  className={isValid || isDuplicate}
                  value={this.state.oldServiceName}
                  onChange={event => this.handleNameChange(event)}
                />
              </Autofocus>
              {this.warningMessages(
                this.state.isServiceNameValid,
                this.state.fullMatchFound,
                this.state.isServiceNameEmpty,
              )}
              <br />

              {!isPrivateCloud(this.props.blazeUserConfig) && (
                <div className="form-group">
                  <div className="mb-3">
                    <div style={{ paddingLeft: '38px' }} className="custom-control custom-switch custom-switch-lg">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="customSwitchFindReplace"
                        defaultChecked={this.state.findAndReplaceToggle}
                        onClick={this.handleToggleChange}
                      />
                      <label
                        style={{ paddingTop: '.32rem' }}
                        className="custom-control-label"
                        htmlFor="customSwitchFindReplace"
                      >
                        {i18n.t('servicePicker.findAndReplace')}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {this.state.findAndReplaceToggle && (
                <div className={'mb-4'}>
                  <FindReplaceList
                    isReadOnly={false}
                    id="find-replace-list"
                    findReplaceList={this.state.findReplaceList}
                    emptyListMessage={i18n.t('transactions:editor.noFindReplace')}
                    onListChange={list => this.setState({ findReplaceList: list })}
                  />
                </div>
              )}
              <div className="form-group">
                <div className="mb-3">
                  <div style={{ paddingLeft: '38px' }} className="custom-control custom-switch custom-switch-lg">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="customSwitchCreateMockService"
                      defaultChecked={this.state.createMsToggle}
                      onClick={this.handleMsToggleChange}
                    />
                    <label
                      style={{ paddingTop: '.32rem' }}
                      className="custom-control-label"
                      htmlFor="customSwitchCreateMockService"
                    >
                      {i18n.t('servicePicker.createMockService')}
                    </label>
                  </div>
                  <p
                    style={{
                      color: '#8E99AB',
                      paddingTop: '10px',
                      paddingLeft: '1px',
                    }}
                  >
                    {i18n.t('servicePicker.redirectMsText')}
                  </p>
                </div>
              </div>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button id={'qa-clone-service-modal-btn-cancel'} variant="link" onClick={this.onCancel}>
            {i18n.t('common:actions.cancel')}
          </Button>
          <button
            type="button"
            id={'qa-clone-service-modal-btn'}
            className="btn btn-primary"
            onClick={this.cloneServiceHandler}
            style={this.getCloneCss()}
            disabled={
              this.state.cloningInProgress ||
              this.validateToggle(
                this.state.findAndReplaceToggle,
                this.state.findReplaceList,
                this.state.fullMatchFound,
                !this.state.isServiceNameValid,
                this.state.isServiceNameEmpty,
              )
            }
          >
            {!this.state.cloningInProgress && i18n.t('servicePicker.clone')}
            {this.state.cloningInProgress && (
              <SimpleLoader loadingText={''} style={{ position: 'relative', top: '-1px', left: '-1px' }} />
            )}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = ({ services, blazeUserConfig }: ApplicationState) => ({
  services: services.services,
  isLoading: services.isLoading,
  blazeUserConfig: blazeUserConfig.blazeUserConfig,
});

const mapDispatchToProps = {
  fetchServices,
  setPageFilter,
  reloadWorkspaceComponents,
  showToastNotification,
};

type MapToStateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = LocalProps & MapToStateProps & DispatchProps & RouteComponentProps<any>;
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CloneServiceModal));
